import React from 'react';
import { useCart } from '../context/CartContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Trash2, ShoppingCart as CartIcon } from 'lucide-react';
import { Badge } from './ui/badge';

const CartDisplay = () => {
  const { cart, removeFromCart, clearCart, getTotalPrice, prepareForOrder } = useCart();

  const formatFlavors = (flavors = []) => {
    if (!flavors.length) {
      return [];
    }

    const flavorCount = flavors.reduce((acc, flavor) => {
      acc[flavor] = (acc[flavor] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(flavorCount).map(([flavor, count]) =>
      count > 1 ? `${flavor} (${count})` : flavor
    );
  };

  const handleFinalize = () => {
    prepareForOrder();
    // Scroll para seção de pedidos
    setTimeout(() => {
      const orderSection = document.getElementById('order');
      if (orderSection) {
        orderSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (cart.length === 0) {
    return (
      <Card className="border-2 border-amber-200 bg-amber-50">
        <CardHeader className="bg-gradient-to-br from-amber-100 to-orange-100 pb-4">
          <CardTitle className="flex items-center gap-2 text-amber-900">
            <CartIcon className="w-5 h-5" />
            Meu Carrinho
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 text-center text-amber-700">
          <p>Nenhum item no carrinho</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
      <CardHeader className="bg-gradient-to-br from-amber-200 to-orange-200 pb-4">
        <CardTitle className="flex items-center gap-2 text-amber-950">
          <CartIcon className="w-5 h-5" />
          Meu Carrinho ({cart.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {cart.map((item, index) => (
            <div key={index} className="bg-white rounded-lg p-4 border border-amber-200">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="font-semibold text-amber-950">
                    {item.quantity} unidades - {item.productType}
                  </p>
                  <p className="text-sm text-amber-700 mb-2">
                    Preço: <span className="font-bold">{item.price}</span>
                  </p>
                  {item.flavors?.length > 0 && (
                    <div className="text-xs text-amber-600">
                      <p className="font-semibold mb-1">Sabores:</p>
                      <div className="flex flex-wrap gap-1">
                        {formatFlavors(item.flavors).map((flavor, i) => (
                          <Badge key={i} variant="secondary" className="bg-amber-100 text-amber-800">
                            {flavor}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFromCart(index)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t-2 border-amber-200">
          <div className="flex justify-between items-center mb-4">
            <span className="font-bold text-amber-950">Total:</span>
            <span className="text-2xl font-bold text-amber-700">€{getTotalPrice()}</span>
          </div>
          
          <div className="space-y-2">
            <Button 
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              onClick={handleFinalize}
            >
              Finalizar Pedido
            </Button>
            <Button 
              variant="outline"
              onClick={clearCart}
              className="w-full border-amber-300 text-amber-800 hover:bg-amber-50"
            >
              Limpar Carrinho
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CartDisplay;
